"use client";

import dynamic from "next/dynamic";
import type { Block } from "@blocknote/core";
import type { LexicalContentBlocks } from "@/lib/article-content-blocks";
import { LexicalArticleRenderer } from "@/components/site/lexical-article-renderer";

const BlockNoteRenderer = dynamic(
  () => import("./blocknote-renderer").then((m) => m.BlockNoteRenderer),
  { ssr: false },
);

function isBlockNoteFormat(blocks: unknown): boolean {
  if (!Array.isArray(blocks) || blocks.length === 0) return false;
  return blocks.every(
    (b) => typeof b === "object" && b !== null && "type" in b && "id" in b,
  );
}

function isLexicalFormat(blocks: unknown): blocks is LexicalContentBlocks {
  return (
    typeof blocks === "object" &&
    blocks !== null &&
    (blocks as { type?: unknown }).type === "lexical" &&
    typeof (blocks as { editorState?: unknown }).editorState === "object"
  );
}

function legacyJsonText(value: unknown): string {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.map(legacyJsonText).filter(Boolean).join("\n\n");
  if (typeof value !== "object" || value === null) return "";
  const raw = value as { type?: unknown; text?: unknown; content?: unknown; children?: unknown; doc?: unknown };
  if (raw.type === "articleImage" || raw.type === "youtubeVideo" || raw.type === "socialEmbed") return "";
  if (typeof raw.text === "string") return raw.text;
  return [legacyJsonText(raw.content), legacyJsonText(raw.children), legacyJsonText(raw.doc)].filter(Boolean).join("\n\n");
}

export function ArticleContentRenderer({
  blocks,
  fallbackContent,
}: {
  blocks: Block[] | unknown | null | undefined;
  fallbackContent?: string | null;
}) {
  if (isLexicalFormat(blocks)) {
    return <LexicalArticleRenderer editorState={blocks.editorState} />;
  }

  if (isBlockNoteFormat(blocks)) {
    return <BlockNoteRenderer blocks={blocks as Block[]} />;
  }

  const legacyText = legacyJsonText(blocks);
  const fallbackText = legacyText || fallbackContent || "";

  return (
    <div className="clear-both max-w-none break-words text-base leading-7 [overflow-wrap:anywhere] sm:text-lg sm:leading-8">
      {fallbackText.split("\n").filter(Boolean).map((paragraph, i) => (
        <p key={i} className="my-5 max-w-3xl">
          {paragraph}
        </p>
      ))}
    </div>
  );
}
